import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAllVacanciesByCompanyId } from "@/hooks/queries/usevacancy";
import Dropdown from "@/components/ui/Dropdown";
import { Building2, Calendar, Filter, Loader2 } from "lucide-react";
import Interview from "@/components/Interview/Interview";
import { calendlyEvent } from "@/services/caldenlyEvent";
import { InterViewSlotService } from "@/services/InterViewSlot";

const InterviewSlots = () => {
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [recruiters, setRecruiters] = useState([]);
  const { id: companyId } = useParams();
  const { data: vacancies } = useAllVacanciesByCompanyId(companyId);
  const [eventsData, setEventsData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Generate company-specific localStorage keys
  const LOCAL_STORAGE_KEYS = {
    VACANCY: `selectedVacancy_${companyId}`,
    STATUS: `selectedStatus_${companyId}`,
    DATE: `selectedDate_${companyId}`,
  };

  const fetchRecruiters = async (vacancy) => {
    try {
      const response = await calendlyEvent.getRecruiters(vacancy.id);
      setRecruiters(response?.recruiters ?? []);
    } catch (error) {
      console.error("Error fetching recruiters:", error);
    }
  };

  const fetchSlots = async (vacancy, date) => {
    try {
      setIsLoading(true);
      const slots = await InterViewSlotService.getAllSlots(vacancy.id, date);
      setEventsData(slots);
      console.log("Fetched slots:", slots);
    } catch (error) {
      console.error("Error fetching slots:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVacancySelect = async (vacancy) => {
    setSelectedVacancy((prev) => {
      if (prev?.id !== vacancy?.id) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.VACANCY,
          JSON.stringify(vacancy),
        );
      }
      return vacancy;
    });

    setSelectedStatus(null);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.STATUS);

    await fetchRecruiters(vacancy);
    if (selectedDate) {
      await fetchSlots(vacancy, selectedDate);
    }
  };

  const handleStatusSelect = async (status) => {
    setSelectedStatus(status);
    localStorage.setItem(LOCAL_STORAGE_KEYS.STATUS, JSON.stringify(status));

    if (selectedDate) {
      try {
        setIsLoading(true);
        const events_data = await calendlyEvent.getCalendlyEvents(
          status.vacancy_status_id,
          { start_time: selectedDate },
        );
        setEventsData([events_data]);
        console.log("Fetched Calendly events:", events_data);
      } catch (error) {
        console.error("Error fetching Calendly events:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DATE, date);
  };

  const fetchEventsData = async () => {
    if (!selectedVacancy || !selectedDate) return;

    if (selectedStatus) {
      try {
        setIsLoading(true);
        const events_data = await calendlyEvent.getCalendlyEvents(
          selectedStatus.vacancy_status_id,
          { start_time: selectedDate },
        );
        setEventsData([events_data]);
        console.log("Fetched Calendly events:", events_data);
      } catch (error) {
        console.error("Error fetching Calendly events:", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      await fetchSlots(selectedVacancy, selectedDate);
    }
  };

  const assignEventToRecruiter = async (eventId, recruiterId) => {
    try {
      const response = await calendlyEvent.assignEvent(recruiterId, eventId);
      if (response) {
        await fetchEventsData();
      }
    } catch (error) {
      console.error("Error assigning event to recruiter:", error);
    }
  };

  // Restore saved state on component mount
  useEffect(() => {
    const savedVacancy = localStorage.getItem(LOCAL_STORAGE_KEYS.VACANCY);
    const savedStatus = localStorage.getItem(LOCAL_STORAGE_KEYS.STATUS);
    const savedDate = localStorage.getItem(LOCAL_STORAGE_KEYS.DATE);

    if (savedVacancy) {
      const parsedVacancy = JSON.parse(savedVacancy);
      setSelectedVacancy(parsedVacancy);
      fetchRecruiters(parsedVacancy);
    }

    if (savedStatus) {
      const parsedStatus = JSON.parse(savedStatus);
      setSelectedStatus(parsedStatus);
    }

    if (savedDate) {
      setSelectedDate(savedDate);
    }
  }, [companyId]);

  // Fetch events data when selectedVacancy or selectedDate changes
  useEffect(() => {
    fetchEventsData();
  }, [selectedVacancy, selectedDate]);

  return (
    <div className="w-full">
      <div className="flex flex-row justify-between items-center  gap-4">
        <div className="flex flex-row justify-start items-center gap-4">
          <Dropdown
            options={
              vacancies?.vacancies?.map((vacancy) => ({
                label: vacancy.title,
                value: vacancy,
              })) || []
            }
            value={
              selectedVacancy
                ? { label: selectedVacancy.title, value: selectedVacancy }
                : null
            }
            onChange={(selected) => handleVacancySelect(selected.value)}
            placeholder="Select Vacancy"
            icon={<Building2 className="w-5 h-5 text-foreground flex-shrink-0" />}
            searchable={true}
            renderOption={(option) => (
              <p className="text-foreground">{option.label}</p>
            )}
          />
          {selectedVacancy && (
            <Dropdown
              options={
                selectedVacancy?.vacancy_statuses?.map((status) => ({
                  label: status.status.name,
                  value: { ...status.status, vacancy_status_id: status.id },
                })) || []
              }
              value={
                selectedStatus
                  ? { label: selectedStatus.name, value: selectedStatus }
                  : null
              }
              onChange={(selected) => handleStatusSelect(selected.value)}
              placeholder="Status"
              icon={<Filter className="w-5 h-5 text-foreground flex-shrink-0" />}
              searchable={true}
              className="w-64"
              dropdownWidth="w-64"
              renderOption={(option) => (
                <p className="text-foreground">{option.label}</p>
              )}
            />
          )}
        </div>

        <div className="flex flex-row justify-start items-center gap-4">
          <div className="flex items-center gap-2 bg-card rounded-lg p-3 border border-input">
            <Calendar className="w-5 h-5 text-foreground" />
            <input
              type="date"
              value={selectedDate || ""}
              onChange={(e) => handleDateChange(e.target.value)}
              className="bg-transparent w-full focus:outline-none text-foreground"
            />
          </div>
          <button
            className="bg-primary text-white px-4 py-2 rounded-md hidden"
            onClick={() => fetchEventsData()}
          >
            Search
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <Loader2 className="w-16 h-16 animate-spin" />
        </div>
      ) : (
        <Interview
          eventsData={eventsData}
          recruiters={recruiters}
          assignEvent={assignEventToRecruiter}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default InterviewSlots;
